<template>
	<v-app id="app">
		<v-main>
			<div :class="isPublic ? 'public-layout' : 'auth-layout'">
				<SystemBanner />
				<!-- Keep existing notification for backward compatibility -->
				<RateLimitNotification v-if="!useMultiEndpointNotifications" />
				<!-- New multi-endpoint notifications -->
				<MultiEndpointRateLimitNotification v-if="useMultiEndpointNotifications" />
				<div
					:class="publicNav ? 'navigation-static' : 'navigation'"
					v-if="$route.path !== '/cross-reference' && $route.path !== '/vendor-search'"
					ref="navigation"
				>
					<Navigation
						@navHeight="
							($event) => {
								transformNav($event);
							}
						"
					/>
				</div>
				<v-container
					v-if="$route.path !== '/cross-reference'"
					:class="isPublic ? 'app-content' : 'app-content px-5 mt-4'"
					:style="{ paddingTop: paddingTop }"
				>
					<keep-alive max="5" include="Mail">
						<router-view :key="$route.path"></router-view>
					</keep-alive>
				</v-container>
				<div
					v-else
					:class="isPublic ? 'app-content' : 'app-content cross-ref px-5 mt-4'"
					:style="{ paddingTop: paddingTop }"
				>
					<keep-alive max="5" include="Mail">
						<router-view :key="$route.path"></router-view>
					</keep-alive>
				</div>
			</div>
		</v-main>
		<AssistantDrawer v-if="assistantAvailable" />
		<!-- Add the VendorStyle component if we have a valid vendor -->
		<VendorStyle v-if="vendorData" :vendor="vendorData" />
	</v-app>
</template>

<script>
import Navigation from "@/components/Navigation.vue";
import SystemBanner from "@/components/SystemBanner.vue";
import VendorStyle from "@/components/VendorStyle.vue";
import RateLimitNotification from "@/components/RateLimitNotification.vue";
import MultiEndpointRateLimitNotification from "@/components/MultiEndpointRateLimitNotification.vue";
import AssistantDrawer from "@/components/Assistant/AssistantDrawer.vue";
import { PUBLIC_NAV_PATHS, usesPublicNav } from "@/utils/publicNav";

export default {
	components: {
		AssistantDrawer,
		Navigation,
		SystemBanner,
		VendorStyle,
		RateLimitNotification,
		MultiEndpointRateLimitNotification,
	},
	data() {
		return {
			paddingTop: "0px",
			isPartnerDomain: false,
			vendorData: null,
			// Feature flag for multi-endpoint notifications (can be toggled for gradual rollout)
			useMultiEndpointNotifications: true,
		};
	},
	methods: {
		transformNav(paddingTop) {
			if (!this.isPartnerDomain) {
				this.paddingTop = paddingTop;
			} else {
				this.paddingTop = "0px";
			}
		},
	},
	computed: {
		assistantAvailable() {
			return (
				this.$store.getters.isLoggedIn && this.$store.getters["assistant/isAvailable"] && !this.isPublic
			);
		},
		publicNav() {
			return usesPublicNav(this.$route.path, this.$store.getters.isLoggedIn);
		},
		isPublic() {
			const path = this.$route.path;
			return PUBLIC_NAV_PATHS.includes(path) || path === "/reset-password" || path === "/forgot-password";
		},
	},
	watch: {
		// Clear the drawer when the user signs out so the next user in this tab never sees a previous transcript.
		"$store.getters.isLoggedIn"(loggedIn) {
			if (!loggedIn) this.$store.dispatch("assistant/resetAll");
		},
	},
	async beforeCreate() {
		// Check if we're in an iframe
		const isInIframe = window.self !== window.top;

		if (document.referrer) {
			const parentOrigin =
				window.location.ancestorOrigins?.[0] ||
				(document.referrer ? new URL(document.referrer).origin : null);

			if (parentOrigin) {
				document.cookie = `parentOrigin=${encodeURIComponent(parentOrigin)};path=/`;
				try {
					const res = await this.$http.post("/api/auth/validate-iframe-origin", {
						referrer: parentOrigin,
					});
					if (res.data && res.data.vendor) {
						this.isPartnerDomain = true;
						this.transformNav("0px");

						// Store the vendor data to use for styling
						this.vendorData = res.data.vendor;

						// Also store in Vuex store for global access
						this.$store.commit("setVendorData", res.data.vendor);

						// If we're in an iframe with valid vendor domain,
						// push directly to the vendor search page without login
						if (isInIframe && this.$route.path !== "/vendor-search") {
							this.$router.push("/vendor-search");
						}
					}
				} catch (err) {
					console.error("Error validating parent domain:", err);
				}
			}
		}

		// Only attempt login if not in iframe mode and not on password reset pages
		const isPasswordResetPage =
			this.$route.path === "/reset-password" || this.$route.path === "/forgot-password";

		// Skip login verification for password reset routes or iframes
		if (!isInIframe && !isPasswordResetPage) {
			// Check if not already logged in - router guard will handle the rest for protected routes
			if (!this.$store.getters.isLoggedIn) {
				// Try to restore session - but don't redirect - let router handle that
				await this.$store.dispatch("verifySession");
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.v-main {
	padding: 0 !important;
	.container {
		padding-bottom: 0 !important;
	}
}

.public-layout {
	width: -webkit-fill-available;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.auth-layout {
	width: -webkit-fill-available;
	display: flex;
	justify-content: flex-start;
}
</style>

<!-- GLOBAL STYLES -->
<style lang="scss">
@import "./sass/main.scss";

.navigation {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: 9999;
}

/* The assistant drawer overlays the right edge on desktop (AssistantDrawer.vue sets the class and --assistant-width); keep the
   signed-in menu out from under it so Logout stays reachable, wrapping the links if needed. */
html.assistant-open .navigation #menu {
	max-width: calc(100% - var(--assistant-width, 460px));
	flex-wrap: wrap;
}

/* Public pages: the header is a sticky element in the document flow; the wrapper must not
   create its own box or the header would have nothing to stick within. */
.navigation-static {
	display: contents;
}

.app-content {
	transition: padding-top 0.3s;
	padding-left: 0 !important;
	padding-right: 0 !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
	margin-top: 0 !important;
}
</style>
